/**
 * Plaid Health Board Hooks
 *
 * Custom hooks for the global Plaid health ops board.
 *
 * @fileoverview React hooks for admin Plaid health operations
 */

import { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../services/adminApi';
import { PlaidHealthBoardResponse } from '../types/admin';
import { toUserMessage } from '../lib/apiError';

/**
 * Hook for the global Plaid health board
 *
 * Fetches all tenants with LOGIN_REQUIRED or UNKNOWN tokens.
 * Supports manual refresh.
 */
export function usePlaidHealthBoard() {
  const [data, setData] = useState<PlaidHealthBoardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHealthBoard = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await adminApi.plaid.getHealthBoard();
      setData(result);
    } catch (err) {
      setError(toUserMessage(err, "We couldn't load the Plaid health board. Please try again."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealthBoard();
  }, [fetchHealthBoard]);

  return {
    data,
    loading,
    error,
    refetch: fetchHealthBoard,
  };
}

/**
 * Hook for sending a reconnect email to a single tenant from the health board
 */
export function useSendReconnectEmail() {
  const [sending, setSending] = useState<Record<string, boolean>>({});
  const [results, setResults] = useState<Record<string, { success: boolean; message: string } | null>>({});

  const sendEmail = useCallback(async (tenantId: string) => {
    setSending((prev) => ({ ...prev, [tenantId]: true }));
    setResults((prev) => ({ ...prev, [tenantId]: null }));

    try {
      const result = await adminApi.tenants.sendReconnectEmail(tenantId);
      setResults((prev) => ({ ...prev, [tenantId]: result }));
      return result;
    } catch (err) {
      const errorResult = {
        success: false,
        message: toUserMessage(err, "We couldn't send the email. Please try again."),
      };
      setResults((prev) => ({ ...prev, [tenantId]: errorResult }));
      return errorResult;
    } finally {
      setSending((prev) => ({ ...prev, [tenantId]: false }));
    }
  }, []);

  return {
    sending,
    results,
    sendEmail,
  };
}
